import type { ExportColumn } from '@app/src/types/export'
import { insertIf } from '../utils/helpersTs'
import { Solutions } from '../wf-constants'

const COMPANY_TRANSLATION_ID = 'export.schemas.provider.company'

type Excludes = 'ownerUsers'

// Sending the period the drawer shows keeps the Excel header and values on that same period.
export const providerSpendExportPath = (spendPeriod?: string) =>
  spendPeriod ? `spends[?(@.periodname=='${spendPeriod}')].amount` : 'spend'

export const getProviderExportColumns = (
  solution?: string,
  basePath?: string,
  excludes?: Excludes[],
  includeOrganizationId?: boolean,
  spendPeriod?: string,
): ExportColumn[] => [
  ...insertIf(Boolean(includeOrganizationId), {
    translationId: 'export.schemas.provider.organization.connectedOrganizationId',
    exportPath: basePath ? `${basePath}.organizationId` : 'organizationId',
  }),
  {
    translationId: basePath ? `${COMPANY_TRANSLATION_ID}.id` : 'general.id',
    exportPath: basePath ? `${basePath}.id` : 'id',
  },
  {
    translationId: basePath ? `${COMPANY_TRANSLATION_ID}.name` : 'schemas.supplier.name',
    exportPath: basePath ? `${basePath}.name` : 'name',
  },
  {
    translationId: 'export.schemas.referralContact.referralContactName',
    exportPath: basePath ? `${basePath}.referralContact.name` : 'referralContact.name',
  },
  {
    translationId: 'export.schemas.referralContact.referralContactEmail',
    exportPath: basePath ? `${basePath}.referralContact.email` : 'referralContact.email',
  },
  ...insertIf(!excludes || !excludes.includes('ownerUsers'), {
    translationId: basePath ? `${COMPANY_TRANSLATION_ID}.ownerUsers` : 'schemas.provider.ownerUsersNames',
    exportPath: basePath ? `${basePath}.ownerUsers.name` : 'ownerUsers.name',
  }),
  ...insertIf(!excludes || !excludes.includes('ownerUsers'), {
    translationId: basePath ? `${COMPANY_TRANSLATION_ID}.ownerUserEmails` : 'schemas.provider.ownerUserEmails',
    exportPath: basePath ? `${basePath}.ownerUsers.email` : 'ownerUsers.email',
  }),
  {
    translationId: basePath ? `${COMPANY_TRANSLATION_ID}.country` : 'schemas.supplier.country',
    exportPath: basePath ? `${basePath}.country.name` : 'country.name',
  },
  {
    translationId: basePath ? `${COMPANY_TRANSLATION_ID}.websiteAddress` : 'schemas.supplier.websiteAddress',
    exportPath: basePath ? `${basePath}.websiteAddress` : 'websiteAddress',
  },
  {
    translationId: basePath ? `${COMPANY_TRANSLATION_ID}.industry` : 'schemas.organization.industry',
    exportPath: basePath ? `${basePath}.industry.description` : 'industry.description',
  },
  {
    translationId: 'schemas.organization.revenue',
    exportPath: basePath ? `${basePath}.revenueRangeValue` : 'revenueRangeValue',
  },
  {
    translationId: 'schemas.organization.employees',
    exportPath: basePath ? `${basePath}.companySizeRangeValue` : 'companySizeRangeValue',
  },
  {
    translationId: basePath ? `${COMPANY_TRANSLATION_ID}.linkStatus` : 'schemas.supplier.linkStatus',
    exportPath: basePath ? `${basePath}.linkStatus` : 'linkStatus',
  },
  {
    translationId: basePath ? `${COMPANY_TRANSLATION_ID}.vatNumber` : 'schemas.supplier.vatNumber',
    exportPath: basePath ? `${basePath}.vatNumber` : 'vatNumber',
  },
  {
    translationId: basePath ? `${COMPANY_TRANSLATION_ID}.registrationNumber` : 'schemas.supplier.registrationNumber',
    exportPath: basePath ? `${basePath}.registrationNumber` : 'registrationNumber',
  },
  {
    translationId: basePath ? `${COMPANY_TRANSLATION_ID}.createdAt` : 'schemas.supplier.createdAt',
    exportPath: basePath ? `${basePath}.createdAt` : 'createdAt',
  },
  {
    translationId: basePath ? `${COMPANY_TRANSLATION_ID}.duns` : 'schemas.organization.duns',
    exportPath: basePath ? `${basePath}.duns` : 'duns',
  },
  {
    translationId: basePath ? `${COMPANY_TRANSLATION_ID}.customId` : 'schemas.supplier.customId',
    exportPath: basePath ? `${basePath}.customId` : 'customId',
  },
  ...insertIf(solution !== Solutions.Finance, {
    translationId: basePath
      ? `${COMPANY_TRANSLATION_ID}.providerApprovalStatus`
      : 'schemas.supplier.providerApprovalStatus',
    exportPath: basePath ? `${basePath}.providerApprovalStatusValue` : 'providerApprovalStatusValue',
  }),
  ...insertIf(solution !== Solutions.Finance, {
    translationId: basePath ? `${COMPANY_TRANSLATION_ID}.activityStatus` : 'schemas.provider.activityStatus',
    exportPath: basePath ? `${basePath}.providerActivityStatusValue` : 'providerActivityStatusValue',
  }),
  {
    translationId: basePath
      ? `${COMPANY_TRANSLATION_ID}.finalRiskRating`
      : 'export.schemas.provider.company.finalRiskRating',
    exportPath: basePath ? `${basePath}.finalRiskRatingValue` : 'finalRiskRatingValue',
  },
  {
    translationId: basePath ? `${COMPANY_TRANSLATION_ID}.priority` : 'schemas.provider.priority',
    exportPath: basePath ? `${basePath}.providerPriorityValue` : 'providerPriorityValue',
  },
  ...insertIf(solution !== Solutions.Finance, {
    translationId: basePath ? `${COMPANY_TRANSLATION_ID}.supplierUsage` : 'schemas.provider.supplierUsage',
    exportPath: basePath ? `${basePath}.supplierUsageValue` : 'supplierUsageValue',
  }),
  ...insertIf(solution !== Solutions.Finance, {
    translationId: basePath ? `${COMPANY_TRANSLATION_ID}.tier` : 'schemas.provider.tier',
    exportPath: basePath ? `${basePath}.tier` : 'tier',
  }),
  {
    translationId: basePath ? `${COMPANY_TRANSLATION_ID}.customCategories` : 'schemas.supplier.customCategories',
    exportPath: basePath ? `${basePath}.categoryOptionsNames` : 'categoryOptionsNames',
  },
  {
    translationId: 'export.schemas.provider.publicContact.publicContactName',
    exportPath: 'publiccontactname',
  },
  {
    translationId: 'export.schemas.provider.publicContact.publicContactEmail',
    exportPath: 'publiccontactemail',
  },
  spendPeriod
    ? {
        translationId: 'export.schemas.provider.spend',
        translationValues: { year: spendPeriod },
        exportPath: providerSpendExportPath(spendPeriod),
      }
    : { translationId: 'schemas.supplier.annualSpend.spend', exportPath: providerSpendExportPath() },
]
